import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, KeyRound, ScanLine, CheckCircle2, Loader2 } from "lucide-react";
import {
  lookupActivationToken,
  activateBindPasswordAndProvisionMfa,
  activateVerifyMfa,
  setCommunityPartnerToken,
  formatCommunityPartnerError,
} from "@/services/communityPartnerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

const STAGE = {
  LOADING: "loading",
  INVALID: "invalid",
  PASSWORD: "password",
  MFA_ENROLL: "mfa_enroll",
  DONE: "done",
};

/**
 * HC-EPIC-004 · Sprint 1-B · Community Partner activation page.
 *
 * Route: `/community-partner-portal/activate?token=<one-time>`
 *
 * Owner directive §6A + §7 highlights:
 *   - One-time token processing with safe generic errors for invalid,
 *     expired, revoked, or already-used tokens.
 *   - Password establishment + real MFA enrollment via TOTP + QR.
 *   - Successful activation confirmation + safe redirection to
 *     `/community-partner-portal/users`.
 *   - No editable organization ID / role ID / user ID / MFA secret /
 *     token hash is rendered. Sensitive activation state is cleared on
 *     component unmount and after success/failure.
 *   - Activation session token is kept in React state only — never
 *     written to browser storage. The final bearer token is written to
 *     sessionStorage (per-tab), never localStorage.
 */
export default function CommunityPartnerActivate() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [stage, setStage] = useState(STAGE.LOADING);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");

  // In-memory activation session — never persisted.
  const [activationSessionToken, setActivationSessionToken] = useState("");
  const [qrPngDataUrl, setQrPngDataUrl] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  // Owner directive §7: the activation token must not remain in the
  // visible address bar / browser history / Referer. Immediately strip
  // it via history.replaceState. The token was already captured into
  // component state (`token`) on the first render, so removing it from
  // the URL does not affect the flow.
  useEffect(() => {
    if (typeof window !== "undefined"
        && typeof window.history?.replaceState === "function"
        && window.location.search.includes("token")) {
      try {
        window.history.replaceState(
          null, "", window.location.pathname
        );
      } catch { /* no-op */ }
    }
  }, []);

  // Clear any residual portal token as soon as the page mounts so the
  // user experiences a clean activation regardless of prior state.
  useEffect(() => {
    setCommunityPartnerToken(null);
  }, []);

  // Clear all sensitive state when the component unmounts.
  useEffect(() => {
    return () => {
      setPassword("");
      setPasswordConfirm("");
      setActivationSessionToken("");
      setQrPngDataUrl("");
      setMfaCode("");
    };
  }, []);

  // Look up the invitation the moment we know the token.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) {
        setStage(STAGE.INVALID);
        setErr("This link is missing an invitation token.");
        return;
      }
      try {
        const data = await lookupActivationToken(token);
        if (!alive) return;
        if (!data?.valid) {
          setStage(STAGE.INVALID);
          setErr("This invitation link is not valid. It may have expired, been used, or been revoked.");
          setPreview(null);
          return;
        }
        setPreview({
          organization_display_name: data.organization_display_name,
          role_label: data.role_label,
        });
        setStage(STAGE.PASSWORD);
      } catch (e) {
        if (!alive) return;
        setStage(STAGE.INVALID);
        setErr("This invitation link is not valid. It may have expired, been used, or been revoked.");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const passwordValid = useMemo(() => (
    password.length >= 12 && password === passwordConfirm
  ), [password, passwordConfirm]);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!passwordValid || busy) return;
    setBusy(true);
    setErr("");
    try {
      const data = await activateBindPasswordAndProvisionMfa(token, password);
      // Never store the plaintext password — clear immediately.
      setPassword("");
      setPasswordConfirm("");
      setActivationSessionToken(data.activation_session_token);
      // NOTE: the plaintext MFA secret is contained inside the
      // `mfa_provisioning_uri` returned by the backend. We deliberately
      // DO NOT store it in component state — it is not needed once the
      // QR image has been generated by the backend. The QR PNG data URL
      // is the only representation we retain.
      setQrPngDataUrl(data.mfa_qr_png_data_url || "");
      setStage(STAGE.MFA_ENROLL);
    } catch (e) {
      setErr(formatCommunityPartnerError(e, "We could not process this activation."));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (busy || !mfaCode.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const data = await activateVerifyMfa(activationSessionToken, mfaCode.trim());
      // Wipe the transient activation session artefacts before storing
      // the permanent portal token.
      setActivationSessionToken("");
      setQrPngDataUrl("");
      setMfaCode("");
      setCommunityPartnerToken(data.access_token);
      setStage(STAGE.DONE);
      setTimeout(() => {
        nav("/community-partner-portal/users", { replace: true });
      }, 1200);
    } catch (e) {
      setErr(formatCommunityPartnerError(e, "That code did not work. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-slate-200 p-8"
           data-testid="cp-activate-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="rounded-full bg-emerald-50 text-emerald-700 p-2">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900" data-testid="cp-activate-title">
              Activate your Community Partner account
            </h1>
            {preview?.organization_display_name && (
              <p className="text-sm text-slate-600" data-testid="cp-activate-org-name">
                {preview.organization_display_name}
                {preview.role_label ? ` · ${preview.role_label}` : ""}
              </p>
            )}
          </div>
        </div>

        {stage === STAGE.LOADING && (
          <div className="flex items-center gap-2 text-slate-500" data-testid="cp-activate-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            Checking your invitation…
          </div>
        )}

        {stage === STAGE.INVALID && (
          <Alert variant="destructive" data-testid="cp-activate-invalid">
            <AlertDescription>
              {err || "This invitation is not valid."} Please contact your Community Partner Organization Administrator
              or HarborWell for assistance.
            </AlertDescription>
          </Alert>
        )}

        {stage === STAGE.PASSWORD && (
          <form onSubmit={submitPassword} className="space-y-4" data-testid="cp-activate-password-form">
            <div className="text-sm text-slate-700 flex items-start gap-2">
              <KeyRound className="w-4 h-4 mt-0.5 text-slate-500" />
              <span>Choose a strong password for your Community Partner Portal account.</span>
            </div>
            <div>
              <Label htmlFor="cp-pw">Password</Label>
              <Input id="cp-pw" type="password" autoComplete="new-password"
                     data-testid="cp-activate-password-input"
                     value={password} onChange={(e) => setPassword(e.target.value)}
                     minLength={12} required />
              <p className="text-xs text-slate-500 mt-1">At least 12 characters.</p>
            </div>
            <div>
              <Label htmlFor="cp-pw2">Confirm password</Label>
              <Input id="cp-pw2" type="password" autoComplete="new-password"
                     data-testid="cp-activate-password-confirm-input"
                     value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)}
                     minLength={12} required />
            </div>
            {err && (
              <Alert variant="destructive" data-testid="cp-activate-password-error">
                <AlertDescription>{err}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" disabled={!passwordValid || busy}
                    data-testid="cp-activate-password-submit"
                    className="w-full">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue to MFA setup"}
            </Button>
          </form>
        )}

        {stage === STAGE.MFA_ENROLL && (
          <form onSubmit={submitMfa} className="space-y-4" data-testid="cp-activate-mfa-form">
            <div className="text-sm text-slate-700 flex items-start gap-2">
              <ScanLine className="w-4 h-4 mt-0.5 text-slate-500" />
              <span>
                Scan the code below with your authenticator app
                (Google Authenticator, Microsoft Authenticator, Authy, 1Password).
              </span>
            </div>
            <div className="flex justify-center">
              {qrPngDataUrl && (
                <img src={qrPngDataUrl}
                     alt="MFA enrolment QR code"
                     data-testid="cp-activate-mfa-qr"
                     className="border border-slate-200 rounded-md w-56 h-56" />
              )}
            </div>
            <p className="text-xs text-slate-500 text-center">
              Once your authenticator shows a 6-digit code, enter it below to finish activation.
            </p>
            <div>
              <Label htmlFor="cp-mfa">Authenticator code</Label>
              <Input id="cp-mfa" type="text" inputMode="numeric" autoComplete="one-time-code"
                     pattern="[0-9]*" maxLength={8}
                     data-testid="cp-activate-mfa-input"
                     value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} required />
            </div>
            {err && (
              <Alert variant="destructive" data-testid="cp-activate-mfa-error">
                <AlertDescription>{err}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" disabled={busy || !mfaCode.trim()}
                    data-testid="cp-activate-mfa-submit"
                    className="w-full">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify and activate"}
            </Button>
          </form>
        )}

        {stage === STAGE.DONE && (
          <div className="flex items-center gap-2 text-emerald-700"
               data-testid="cp-activate-done">
            <CheckCircle2 className="w-5 h-5" />
            Activation complete. Taking you to your portal…
          </div>
        )}
      </div>
    </div>
  );
}
